import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search, X, Check } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import { apiService } from '../services/apiService';
import { LoadingSpinner } from './LoadingSpinner';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const SelectorContainer = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const SearchContainer = styled.div`
  padding: 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props.theme.colors.gray50};
  border-radius: 8px;
  padding: 0.5rem 1rem;
  gap: 0.5rem;
`;

const Input = styled.input`
  border: none;
  background: none;
  flex: 1;
  font-size: 1rem;
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
  }
`;

const ProjectList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const ProjectItem = styled.div<{ selected?: boolean }>`
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.selected ? props.theme.colors.primary + '10' : 'transparent'};
  border: 1px solid ${props => props.selected ? props.theme.colors.primary : props.theme.colors.border};

  &:hover {
    background: ${props => props.theme.colors.gray50};
  }
`;

const ProjectInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ProjectCode = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ProjectDetails = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const SelectedIndicator = styled.div`
  color: ${props => props.theme.colors.primary};
`;

interface ProjectSelectorProps {
  onClose: () => void;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({ onClose }) => {
  const { selectedProject, setSelectedProject } = useProject();
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProjects, setFilteredProjects] = useState<any[]>([]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        const response = await apiService.getProjects();
        setProjects(response.data);
        setFilteredProjects(response.data);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  useEffect(() => {
    const filtered = projects.filter(project => {
      const searchLower = searchTerm.toLowerCase();
      return (
        project.code.toLowerCase().includes(searchLower) ||
        project.customer_name.toLowerCase().includes(searchLower) ||
        (project.so_number && project.so_number.toLowerCase().includes(searchLower)) ||
        (project.order_id && project.order_id.toLowerCase().includes(searchLower))
      );
    });
    setFilteredProjects(filtered);
  }, [searchTerm, projects]);

  const handleProjectSelect = (project: any) => {
    setSelectedProject(project);
    onClose();
  };

  return (
    <Overlay>
      <SelectorContainer>
        <Header>
          <Title>Select Project</Title>
          <button onClick={onClose}>
            <X size={24} />
          </button>
        </Header>

        <SearchContainer>
          <SearchInput>
            <Search size={20} />
            <Input
              placeholder="Search by project code, customer, SO number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchInput>
        </SearchContainer>

        <ProjectList>
          {loading ? (
            <LoadingSpinner />
          ) : filteredProjects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              No projects found
            </div>
          ) : (
            filteredProjects.map((project) => (
              <ProjectItem
                key={project.id}
                selected={selectedProject?.id === project.id}
                onClick={() => handleProjectSelect(project)}
              >
                <ProjectInfo>
                  <ProjectCode>{project.code}</ProjectCode>
                  <ProjectDetails>
                    {project.customer_name}
                    {project.so_number && ` • SO: ${project.so_number}`}
                    {project.order_id && ` • Order: ${project.order_id}`}
                  </ProjectDetails>
                </ProjectInfo>
                {selectedProject?.id === project.id && (
                  <SelectedIndicator>
                    <Check size={20} />
                  </SelectedIndicator>
                )}
              </ProjectItem>
            ))
          )}
        </ProjectList>
      </SelectorContainer>
    </Overlay>
  );
}; 